<template>
    <div class="main-content">
        <main>
            <div class="container">
                <section class="hero" id="home">
                    <div class="card">
                        <h2>CoExCell: A Resource for Exploring Gene Coexpression Patterns at Cellular Resolution </h2>
                        <p class="lead"> Explore cell type/spatially resolved coexpression patterns and functional gene groups.</p>
                        <div class="cta">
                            <a class="btn" href="/#/spatial/list">Browse Datasets</a>
                            <a class="btn ghost" href="/#/tutorial">View tutorial</a>
                        </div>
                        <div class="stats">
                            <div class="stat"><div class="value">92</div><div class="label">Single-cell RNA-seq datasets</div></div>
                            <div class="stat"><div class="value">93</div><div class="label">Spatial RNA-seq datasets</div></div>
                            <div class="stat"><div class="value">34</div><div class="label">Tissues</div></div>
                            <div class="stat"><div class="value">445</div><div class="label">Cell Types</div></div>
                        </div>
                    </div>
                    <div class="panel">
                        <h3 style="margin-top:0;color:#0c1826;">Quick gene query</h3>
                        <p style="color:var(--muted);margin-top:6px;">Search a gene symbol to view its co-expressed genes and associated modules in single cell and spatial datasets.</p>
                        <form @submit.prevent="handleSearch">
                            <input class="search-input" placeholder="e.g., APOE" v-model="searchKeyword">
                            <div style="display:flex;gap:10px;margin-top:12px;">
                                <button class="btn" type="submit" style="flex:1;">Search</button>
                            </div>
                        </form>
                        <div class="examples">Example: <span class="pill" @click="fillSearch('APOE')">APOE</span><span class="pill" @click="fillSearch('IRF7')">IRF7</span><span class="pill" @click="fillSearch('COL1A1')">COL1A1</span></div>
                    </div>
                </section>

                <section class="overview-section" id="overview">
                    <div class="overview-container">
                        <div class="overview-copy">
                            <h2>CoExCell Overview</h2>
                            <p class="overview-lead">Welcome to CoExCell - an interactive web resource for cell type-specific and spatially informed gene coexpression across human and mouse tissues.</p>
                            <p>CoExCell is, to our knowledge, the first comprehensive database to integrate 185 single-cell and spatial transcriptomic datasets spanning 34 tissues and 445 unique cell types. Single-cell coexpression networks were constructed within defined cell populations using a noise-aware statistical framework, while spatial coexpression was derived from aggregated meta-spot profiles, enabling biologically meaningful insights that are not confounded by cell-type composition differences.</p>
                            <p>Users can explore the resource through flexible gene-based and dataset-based queries. For any gene, retrieve its coexpression partners, module memberships, functional annotations, and cell type-specific patterns. For each dataset or cell type, browse full coexpression networks, module summaries, pathway enrichment results, candidate transcription factors, and interactive visualizations. The platform also supports comparisons across cell types, tissues, and species, highlighting context-dependent coexpression relationships.</p>
                            <p>Whether you are investigating gene function, identifying regulatory networks, or prioritizing disease-associated genes, CoExCell provides a user-friendly platform to explore coordinated gene expression at cellular and spatial resolution beyond the limits of traditional bulk-tissue coexpression resources.</p>
                        </div>
                        <div class="overview-image-wrap">
                            <img class="overview-image" src="/images/home_intro.jpg" alt="CoExCell overview workflow and feature summary">
                        </div>
                    </div>
                </section>

                <section class="atlas-section" id="atlas">
                    <div class="atlas-container">
                        <h2 style="margin-top:0;color:#0c1826;font-family:'Playfair Display',serif;font-size:36px;margin-bottom:24px;">Choose a Tissue</h2>
                        <p style="color:var(--muted);margin-bottom:32px;max-width:600px;margin-left:auto;margin-right:auto;">Explore cell-type specific co-expression data in a tissue.</p>
                        <object data="/images/Frame 10.svg" type="image/svg+xml" style="width:100%;max-width:100%;height:auto;" ref="atlasSvg">
                            <p>Your browser does not support SVG. Please use a modern browser to view the organ atlas.</p>
                        </object>
                    </div>
                </section>

                <section class="log-section" id="log">
                    <div class="log-heading">
                        <div><span class="eyebrow">DATABASE HISTORY</span><h2>Version log</h2></div>
                        <p>CoExCell is updated as new coexpression data and analysis views become available.</p>
                    </div>
                    <div class="log-list">
                        <div class="log-entry" v-for="entry in versionLog" :key="entry.date">
                            <span class="log-date">{{ entry.date }}</span><span class="log-marker"></span>
                            <div><h3>{{ entry.title }}</h3><p>{{ entry.description }}</p></div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    </div>
</template>

<script>

export default {
  name: "home",
  data() {
    return {
      searchKeyword: "",
      searchResults: [],
      showDropdown: false,
      searchTimeout: null,
      showAllVersions: false,
      // 移除不需要的版本历史数据，因为我们使用的是参考设计的简化版本
      versionEntries: [],
      versionLog: [
        { date: '2025/09', title: 'Added single-cell coexpression data', description: 'Introduced cell type-specific coexpression results for single-cell RNA-seq datasets.' },
        { date: '2025/12', title: 'Added spatial coexpression data', description: 'Added spatially resolved coexpression results and dataset views.' },
        { date: '2026/03', title: 'V1.0 release', description: 'Added coexpression module data and functional annotations.' },
        { date: '2026/10', title: 'V1.1 release', description: 'Updated coexpression results, added cross-dataset consensus scores and a full correlation download page.' }
      ]
    };
  },

  computed: {
    visibleVersionEntries() {
      return this.showAllVersions ? this.versionEntries : this.versionEntries.slice(0, 5);
    }
  },

  created() {
  },

  mounted() {
    this.$nextTick(() => {
      const svgObj = this.$refs.atlasSvg;
      if (svgObj) {
        // Function to bind clicks
        const bindClicks = () => {
          const svgDoc = svgObj.contentDocument;
          if (svgDoc) {
            const links = svgDoc.querySelectorAll('a');
            links.forEach(link => {
              link.style.cursor = 'pointer';
              // Check if already bound or just bind
              link.onclick = (e) => {
                e.preventDefault();
                const tissueName = link.textContent.trim();
                if (tissueName) {
                  this.$router.push({
                    path: '/singleCell/list',
                    query: { tissue: tissueName }
                  });
                }
              };
            });
          }
        };

        // If already loaded
        if (svgObj.contentDocument && svgObj.contentDocument.readyState === 'complete') {
          bindClicks();
        }
        
        // Listen for future loads
        svgObj.addEventListener('load', bindClicks);
      }
    });
  },
  methods: {
    handleSearch() {
      this.$router.push({
        path: '/gene/list',
        query: {
          keyword: this.searchKeyword
        }
      })
    },

    fillSearch(keyword) {
      this.searchKeyword = keyword;
      this.handleSearch();
    },

    handleSearchInput() {
      // 清除之前的定时器
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // 设置新的定时器，防止频繁请求
      this.searchTimeout = setTimeout(() => {
        this.fetchSearchResults();
      }, 300);
    },

    async fetchSearchResults() {
      if (!this.searchKeyword || this.searchKeyword.trim().length < 2) {
        this.searchResults = [];
        return;
      }

      try {
        const response = await this.$ajax.get('gwas/page', {
          type: 1,
          pageNum: 1,
          pageSize: 10,
          keyword: this.searchKeyword
        });

        if (response && response.data && response.data.records) {
          this.searchResults = response.data.records;
          this.showDropdown = true;
        }
      } catch (error) {
        console.error('Error fetching search results:', error);
      }
    },

    selectSearchResult(result) {
      this.searchKeyword = result.trait;
      this.showDropdown = false;
      this.$router.push({
        path: `/gwas/detail/${result.id}`
      });
    },

    handleBlur() {
      // 延迟关闭下拉框，以便点击事件可以先触发
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    },

    toggleVersionHistory() {
      this.showAllVersions = !this.showAllVersions;
    }
  },
};
</script>

<style>
/* :root variables moved to common/base.less */

* {
    box-sizing: border-box;
}

#app, body {
    margin: 0;
    font-family: 'Inter', sans-serif;
    background: #dfe4ed !important;
    color: #0e1a28 !important;
}
</style>

<style scoped>

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Playfair+Display:wght@600&display=swap');

/* Top Navigation */
/* Main Content */
.main-content {
    margin-top: 0;
    padding: 0;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
}

main {
    padding: 32px 0 64px;
}

.container {
    max-width: 1180px;
    margin: 0 auto;
    padding: 0 20px;
}

a {
    text-decoration: none;
    color: inherit;
}

/* Hero Section */
.hero {
    display: flex;
    gap: 26px;
    justify-content: center;
    align-items: stretch;
    margin: 28px 0;
    background: none;
    padding: 0;
    box-shadow: none;
    color: var(--navy);
}

.card {
    flex: 1;
    background: linear-gradient(135deg, #f9fbfe 0%, #d5deea 72%);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 38px;
    box-shadow: var(--shadow);
    text-align: center;
}

.panel {
    flex: 0.9;
    background: linear-gradient(180deg, #f9fbfe 0%, #d8e1ee 100%);
    border: 1px solid var(--border);
    border-radius: 18px;
    padding: 28px;
    box-shadow: var(--shadow);
}

.tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 13px;
    border-radius: 999px;
    background: rgba(93, 135, 181, 0.18);
    color: var(--accent);
    font-weight: 700;
    border: 1px solid rgba(93, 135, 181, 0.36);
    text-transform: uppercase;
    letter-spacing: 0.42px;
}

h1 {
    margin: 0;
    font-size: 52px;
    line-height: 1.06;
    font-family: 'Playfair Display', serif;
    color: #0c1826;
    letter-spacing: 0.3px;
}

.lead {
    color: var(--muted);
    font-size: 17.5px;
    line-height: 1.72;
    max-width: 820px;
    margin: 14px auto 0;
}

.cta {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 16px;
}

.stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 14px;
    margin: 24px auto 0;
    max-width: 780px;
    text-align: center;
}

.stat {
    padding: 15px;
    border-radius: 15px;
    border: 1px solid var(--border);
    background: linear-gradient(180deg, rgba(93, 135, 181, 0.13), rgba(93, 135, 181, 0.08));
    box-shadow: 0 12px 24px rgba(14, 26, 40, 0.16);
}

.stat .value {
    font-size: 25px;
    font-weight: 800;
    color: #0c1826;
}

.stat .label {
    color: var(--muted);
    font-weight: 700;
    font-size: 13px;
}

.search-input {
    width: 100%;
    padding: 16px 17px;
    border: 1px solid var(--border);
    border-radius: 16px;
    font-size: 17px;
    background: #e4ebf5;
    color: var(--navy);
}

.btn {
    padding: 12px 20px;
    border-radius: 13px;
    font-weight: 700;
    border: 1px solid var(--accent);
    background: var(--accent);
    color: #0c1724;
    box-shadow: 0 12px 24px rgba(93, 135, 181, 0.22);
}

.ghost {
    background: transparent;
    color: var(--accent);
    border: 1px solid var(--accent);
    box-shadow: none;
}

.log-section {
    margin: 24px 0 0;
    padding: 42px 38px 48px;
    background: rgba(247, 249, 252, .72);
    border-top: 1px solid var(--border);
}

.log-heading { display: flex; justify-content: space-between; gap: 32px; align-items: flex-end; margin-bottom: 30px; }
.log-heading h2 { margin: 5px 0 0; font-family: 'Playfair Display', serif; color: var(--navy); font-size: 30px; }
.log-heading p { max-width: 390px; margin: 0; color: var(--muted); line-height: 1.65; }
.eyebrow { color: var(--accent); font-size: 11px; letter-spacing: 1.2px; font-weight: 700; }
.log-list { max-width: 820px; }
.log-entry { display: grid; grid-template-columns: 86px 18px 1fr; gap: 16px; align-items: start; min-height: 74px; }
.log-date { color: var(--accent); font-size: 13px; font-weight: 700; padding-top: 2px; }
.log-marker { width: 10px; height: 10px; margin-top: 5px; border: 2px solid var(--accent); border-radius: 50%; background: #f7f9fc; position: relative; }
.log-entry:not(:last-child) .log-marker:after { content: ''; position: absolute; width: 1px; height: 72px; top: 8px; left: 2px; background: var(--border); }
.log-entry h3 { margin: 0 0 5px; color: var(--navy); font-size: 16px; }
.log-entry p { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.55; }
@media (max-width: 720px) { .log-section { padding: 32px 22px; } .log-heading { display: block; } .log-heading p { margin-top: 12px; } }

.examples {
    margin-top: 16px;
    padding: 12px;
    border-radius: 12px;
    background: #d9e3f0;
    border: 1px solid #c5d3e4;
    font-size: 13px;
    color: var(--navy);
}

.pill {
    display: inline-block;
    padding: 7px 10px;
    border-radius: 999px;
    background: #d3deec;
    color: var(--navy);
    border: 1px solid var(--border);
    font-size: 12px;
    margin-right: 6px;
    margin-top: 6px;
    cursor: pointer;
}

@media(max-width: 1080px) {
    .hero {
        flex-direction: column;
    }
}

.atlas-section {
    margin: 48px 0;
    text-align: center;
}

.atlas-container {
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 32px;
    box-shadow: var(--shadow);
    overflow: auto;
}

.atlas-container object {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
}

.atlas-container svg {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
}

.atlas-container svg a:hover text {
    fill: var(--accent) !important;
    transition: fill 0.2s ease;
}

.atlas-container svg a {
    cursor: pointer;
}

.overview-section {
    margin: 48px 0 0;
}

.overview-container {
    background: linear-gradient(180deg, #f9fbfe 0%, #d8e1ee 100%);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 32px;
    box-shadow: var(--shadow);
}

.overview-copy {
    text-align: center;
    margin-bottom: 24px;
}

.overview-copy h2 {
    margin: 0 0 12px;
    color: #0c1826;
    font-family: 'Playfair Display', serif;
    font-size: 36px;
}

.overview-copy p {
    margin: 0 auto;
    max-width: 780px;
    color: var(--muted);
    line-height: 1.7;
    font-size: 16px;
    text-align: left;
}

.overview-copy p + p {
    margin-top: 16px;
}

.overview-lead {
    font-size: 18px;
    color: #0c1826;
    font-weight: 600;
}

.overview-image-wrap {
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(93, 135, 181, 0.24);
    background: rgba(255, 255, 255, 0.72);
    box-shadow: 0 16px 32px rgba(14, 26, 40, 0.12);
}

.overview-image {
    display: block;
    width: 100%;
    height: auto;
}

@media(max-width: 768px) {
    .overview-container {
        padding: 20px;
    }

    .overview-copy h2 {
        font-size: 28px;
    }

    .overview-copy p {
        font-size: 15px;
    }
}

/* Enhanced Search Box */
.search-section {
    padding: 18px 0 10px 0;
    display: flex;
    justify-content: center;
    background: none;
}

.search-box {
    display: flex;
    align-items: stretch;
    background: #fff;
    border-radius: 32px;
    box-shadow: 0 2px 16px rgba(36, 94, 70, 0.08);
    border: 1.5px solid #e0e0d8;
    max-width: 1020px;
    width: 520px;
    overflow: visible; /* 修改为visible以显示下拉菜单 */
    transition: box-shadow 0.2s;
}

.search-box:focus-within {
    box-shadow: 0 4px 24px rgba(36, 94, 70, 0.16);
    border-color: #bfa046;
}

.search-box input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 1em;
    padding: 0 16px;
    background: transparent;
    font-family: "Inter", sans-serif;
    color: var(--primary);
    border-radius: 32px 0 0 32px;
    height: 54px;
    min-width: 0;
    width: 100%;
}

.search-box input::placeholder {
    color: #7ba892;
    opacity: 1;
    font-style: italic;
    font-size: 1em;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.search-box button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--accent);
    color: #0c1724;
    border: none;
    border-radius: 0 32px 32px 0;
    padding: 0 32px;
    font-size: 1.1em;
    font-weight: 700;
    cursor: pointer;
}

.search-box button:hover {
    background: var(--navy);
    box-shadow: 0 2px 12px rgba(14, 26, 40, 0.2);
    transform: translateY(-1px) scale(1.03);
}

.search-box button .icon {
    display: inline-block;
    margin-right: 8px;
    font-size: 1.2em;
    vertical-align: middle;
}

/* Quick Access Optimization */
.quick-access {
    display: flex;
    justify-content: center;
    gap: 32px;
    padding: 18px 0 10px 0;
    margin-bottom: 10px;
}

.quick-link {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: "Inter", sans-serif;
    background: #fff;
    border: var(--border-light);
    border-radius: 22px;
    padding: 18px 36px 14px 36px;
    color: var(--primary);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(.4, 0, .2, 1);
    font-size: 1.13em;
    letter-spacing: 0.5px;
    box-shadow: var(--shadow);
    position: relative;
    overflow: hidden;
    min-width: 180px;
}

.quick-link .icon {
    font-size: 1.5em;
    margin-bottom: 6px;
    color: var(--accent);
    transition: color 0.2s;
}

.quick-link .hint {
    font-size: 0.98em;
    color: #6ba48b;
    font-weight: 400;
    margin-top: 4px;
    letter-spacing: 0.1em;
}

.quick-link:hover {
    background: var(--gradient);
    color: #fff;
    box-shadow: var(--shadow-hover);
    transform: translateY(-2px) scale(1.04);
}

.quick-link:hover .icon {
    color: #fff;
}

/* Features Section */
.features-section {
    padding: 25px 15px;
    max-width: 1200px;
    margin: 0 auto 15px auto;
}

.features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    padding: 0 15px;
}

.feature-card {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border: var(--border-light);
    border-radius: 15px;
    padding: 20px;
    text-align: center;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.feature-icon {
    font-size: 1.8em;
    margin-bottom: 10px;
    animation: float 3s ease-in-out infinite;
    color: var(--accent);
}

.feature-title {
    font-family: "Inter", sans-serif;
    font-size: 1.6em;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 6px;
}

.feature-desc {
    font-family: "Inter", sans-serif;
    font-size: 1.05em;
    color: #3d4d44;
    line-height: 1.5;
}

/* Graphical Abstract */
.graph-abstract {
    padding: 5px 0;
    background: var(--card-bg);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    border-radius: 15px;
    box-shadow: 0 2px 12px rgba(26, 71, 42, 0.07);
    margin: 0 auto;
    max-width: 900px;
}

.graph-abstract-display {
    border-radius: 15px;
    box-shadow: 0 4px 24px rgba(26, 71, 42, 0.08);
    padding: 8px;
    margin: 0 auto;
    width: 100%;
    max-width: 900px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    animation: fadeInUp 0.8s ease-out 1s backwards;
    transition: var(--transition);
    margin-bottom: 8px;
}

.graph-abstract-display img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
}

/* Version Release History */
.version-history {
    /* padding: 25px 15px; */
    max-width: 1000px;
    margin: 0 auto 25px auto;
}

.section-title {
    font-family: "Inter", sans-serif;
    font-size: 1.8em;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 20px;
    text-align: center;
}

.version-timeline {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border: var(--border-light);
    border-radius: 15px;
    padding: 25px;
    padding-top: 15px;
    padding-bottom: 5px;
    box-shadow: 0 2px 12px rgba(26, 71, 42, 0.07);
}

.version-entry {
    margin-bottom: 5px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(26, 71, 42, 0.1);
}

.version-entry:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
}

.version-toggle {
    text-align: center;
    /* margin-top: 15px; */
    color: var(--accent);
    font-weight: 600;
    cursor: pointer;
    padding: 8px;
    transition: all 0.2s ease;
}

.version-toggle:hover {
    color: var(--primary);
    text-decoration: underline;
}

.version-date {
    font-family: "Inter", sans-serif;
    font-size: 1.1em;
    font-weight: 600;
    color: var(--accent);
    margin-bottom: 5px;
}

.version-description {
    font-family: "Inter", sans-serif;
    font-size: 1em;
    color: #3d4d44;
    line-height: 1.5;
}

/* @media (max-width: 768px) {
    .version-history {
        // padding: 20px 10px;
    }

    .section-title {
        font-size: 1.5em;
    }

    .version-timeline {
        padding: 15px;
    }

    .version-date {
        font-size: 1em;
    }

    .version-description {
        font-size: 0.95em;
    }
} */

/* Enhanced Footer */
.footer {
    background: var(--gradient);
    padding: 30px 0;
    position: relative;
    overflow: hidden;
    color: #fff;
    text-align: center;
    margin-top: 15px;
    box-shadow: 0 -4px 20px rgba(26, 71, 42, 0.15);
}

.footer::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    animation: pulse 8s ease-in-out infinite;
}

.footer-content {
    position: relative;
    z-index: 1;
    max-width: 800px;
    margin: 0 auto;
    padding: 0 15px;
    font-size: 1em;
    line-height: 1.35;
    font-family: "Inter", sans-serif;
}

.footer p {
    margin: 4px 0;
    opacity: 0.95;
}

.footer .citation,
.footer .contact {
    margin: 6px 0;
}

.footer .copyright {
    margin-top: 10px;
    font-size: 0.95em;
    opacity: 0.9;
}

.footer a {
    color: #fff;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;
    position: relative;
    padding-bottom: 2px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

.footer a:hover {
    color: var(--accent);
    border-bottom-color: var(--accent);
}

/* @media (max-width: 768px) {
    .hero {
        padding: 35px 0 25px 0;
    }

    .hero h1 {
        font-size: 2.4em;
    }

    .hero p {
        font-size: 1.05em;
        padding: 0 15px;
    }

    .feature-card {
        padding: 15px;
    }

    .feature-title {
        font-size: 1.4em;
    }

    .search-box {
        flex-direction: column;
        padding: 6px;
    }

    .search-box button {
        width: 100%;
        margin: 6px 0 0 0;
    }

    .quick-access {
        gap: 12px;
        padding: 12px;
    }

    .quick-link {
        padding: 8px 20px;
        font-size: 0.95em;
    }

    .graph-abstract {
        padding: 3px 0;
        border-radius: 12px;
    }

    .graph-abstract-display {
        padding: 6px;
        border-radius: 12px;
        margin-bottom: 6px;
    }

    .graph-abstract-display img {
        border-radius: 10px;
    }
} */

@keyframes float {
    0% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-8px);
    }

    100% {
        transform: translateY(0px);
    }
}

@keyframes pulse {
    0% {
        opacity: 0.5;
    }

    50% {
        opacity: 0.8;
    }

    100% {
        opacity: 0.5;
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(15px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 添加搜索下拉框相关样式 */
.search-input-container {
  position: relative;
  flex: 1;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 20px rgba(26, 71, 42, 0.15);
  z-index: 1000;
  border: 1px solid #e0e0d8;
  border-top: none;
}

.search-result-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background-color: var(--primary-light);
}

.result-trait {
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 4px;
}

.result-mesh {
  font-size: 0.9em;
  color: #666;
}

/* 修改搜索框样式以适应下拉菜单 */
.search-box {
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 32px;
  box-shadow: 0 2px 16px rgba(36, 94, 70, 0.08);
  border: 1.5px solid #e0e0d8;
  max-width: 1020px;
  width: 520px;
  overflow: visible; /* 修改为visible以显示下拉菜单 */
  transition: box-shadow 0.2s;
}

.search-box:focus-within {
    box-shadow: 0 4px 24px rgba(36, 94, 70, 0.16);
    border-color: #bfa046;
}

.search-box input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 1em;
    padding: 0 16px;
    background: transparent;
    font-family: "Inter", sans-serif;
    color: var(--primary);
    border-radius: 32px 0 0 32px;
    height: 54px;
    min-width: 0;
}

.search-box input::placeholder {
    color: #7ba892;
    opacity: 1;
    font-style: italic;
    font-size: 1em;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.search-box button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(90deg, #245e46 60%, #2d6a4f 100%);
    color: #fff;
    border: none;
    border-radius: 0 32px 32px 0;
    padding: 0 32px;
    font-size: 1.1em;
    font-weight: 600;
    height: 54px;
    cursor: pointer;
    box-shadow: none;
    transition: background 0.2s, box-shadow 0.2s, transform 0.1s;
    border-left: 1.5px solid #e0e0d8;
    letter-spacing: 0.5px;
}

.search-box button:hover {
    background: linear-gradient(90deg, #1a472a 60%, #245e46 100%);
    box-shadow: 0 2px 12px rgba(36, 94, 70, 0.13);
    transform: translateY(-1px) scale(1.03);
}

.search-box button .icon {
    display: inline-block;
    margin-right: 8px;
    font-size: 1.2em;
    vertical-align: middle;
}

/* Quick Access Optimization */
.quick-access {
    display: flex;
    justify-content: center;
    gap: 32px;
    padding: 18px 0 10px 0;
    margin-bottom: 10px;
}

.quick-link {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: "Inter", sans-serif;
    background: #fff;
    border: var(--border-light);
    border-radius: 22px;
    padding: 18px 36px 14px 36px;
    color: var(--primary);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(.4, 0, .2, 1);
    font-size: 1.13em;
    letter-spacing: 0.5px;
    box-shadow: var(--shadow);
    position: relative;
    overflow: hidden;
    min-width: 180px;
}

.quick-link .icon {
    font-size: 1.5em;
    margin-bottom: 6px;
    color: var(--accent);
    transition: color 0.2s;
}

.quick-link .hint {
    font-size: 0.98em;
    color: #6ba48b;
    font-weight: 400;
    margin-top: 4px;
    letter-spacing: 0.1em;
}

.quick-link:hover {
    background: var(--gradient);
    color: #fff;
    box-shadow: var(--shadow-hover);
    transform: translateY(-2px) scale(1.04);
}

.quick-link:hover .icon {
    color: #fff;
}

/* Features Section */
.features-section {
    padding: 25px 15px;
    max-width: 1200px;
    margin: 0 auto 15px auto;
}

.features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    padding: 0 15px;
}

.feature-card {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border: var(--border-light);
    border-radius: 15px;
    padding: 20px;
    text-align: center;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.feature-icon {
    font-size: 1.8em;
    margin-bottom: 10px;
    animation: float 3s ease-in-out infinite;
    color: var(--accent);
}

.feature-title {
    font-family: "Inter", sans-serif;
    font-size: 1.6em;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 6px;
}

.feature-desc {
    font-family: "Inter", sans-serif;
    font-size: 1.05em;
    color: #3d4d44;
    line-height: 1.5;
}

/* Graphical Abstract */
.graph-abstract {
    padding: 5px 0;
    background: var(--card-bg);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    border-radius: 15px;
    box-shadow: 0 2px 12px rgba(26, 71, 42, 0.07);
    margin: 0 auto;
    max-width: 900px;
}

.graph-abstract-display {
    border-radius: 15px;
    box-shadow: 0 4px 24px rgba(26, 71, 42, 0.08);
    padding: 8px;
    margin: 0 auto;
    width: 100%;
    max-width: 900px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    animation: fadeInUp 0.8s ease-out 1s backwards;
    transition: var(--transition);
    margin-bottom: 8px;
}

.graph-abstract-display img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
}

/* Enhanced Footer */
.footer {
    background: var(--gradient);
    padding: 30px 0;
    position: relative;
    overflow: hidden;
    color: #fff;
    text-align: center;
    margin-top: 15px;
    box-shadow: 0 -4px 20px rgba(26, 71, 42, 0.15);
}

.footer::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    animation: pulse 8s ease-in-out infinite;
}

.footer-content {
    position: relative;
    z-index: 1;
    max-width: 800px;
    margin: 0 auto;
    padding: 0 15px;
    font-size: 1em;
    line-height: 1.35;
    font-family: "Inter", sans-serif;
}

.footer p {
    margin: 4px 0;
    opacity: 0.95;
}

.footer .citation,
.footer .contact {
    margin: 6px 0;
}

.footer .copyright {
    margin-top: 10px;
    font-size: 0.95em;
    opacity: 0.9;
}

.footer a {
    color: #fff;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;
    position: relative;
    padding-bottom: 2px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

.footer a:hover {
    color: var(--accent);
    border-bottom-color: var(--accent);
}

/* @media (max-width: 768px) {
    .hero {
        padding: 35px 0 25px 0;
    }

    .hero h1 {
        font-size: 2.4em;
    }

    .hero p {
        font-size: 1.05em;
        padding: 0 15px;
    }

    .feature-card {
        padding: 15px;
    }

    .feature-title {
        font-size: 1.4em;
    }

    .search-box {
        flex-direction: column;
        padding: 6px;
    }

    .search-box button {
        width: 100%;
        margin: 6px 0 0 0;
    }

    .quick-access {
        gap: 12px;
        padding: 12px;
    }

    .quick-link {
        padding: 8px 20px;
        font-size: 0.95em;
    }

    .graph-abstract {
        padding: 3px 0;
        border-radius: 12px;
    }

    .graph-abstract-display {
        padding: 6px;
        border-radius: 12px;
        margin-bottom: 6px;
    }

    .graph-abstract-display img {
        border-radius: 10px;
    }
} */

@keyframes float {
    0% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-8px);
    }

    100% {
        transform: translateY(0px);
    }
}

@keyframes pulse {
    0% {
        opacity: 0.5;
    }

    50% {
        opacity: 0.8;
    }

    100% {
        opacity: 0.5;
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(15px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
